import React, { useState, useCallback } from "react";
import {
  Box, Dialog, DialogContent, Button, TextField,
  Typography, IconButton, CircularProgress, Chip,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import axiosInstance from "../../../services/axiosInstance";
// What kind of business this is decides which homes it starts with (Stage 4.2b O1b step 7).
import { saveBusiness } from "../ai/useBusiness";
import { BUSINESS_TYPES } from "../ai/businessModules";
import { getProfile } from "./ProfileSlice";
import { getCallFlow } from "./SectionsSlice";
import AddOutlinedIcon      from "@mui/icons-material/AddOutlined";
import CloseIcon            from "@mui/icons-material/Close";
import CheckCircleIcon      from "@mui/icons-material/CheckCircle";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import ApartmentOutlinedIcon from "@mui/icons-material/ApartmentOutlined";
import RocketLaunchOutlinedIcon from "@mui/icons-material/RocketLaunchOutlined";
import { SUPPORTED_CALL_LANGS } from "../../../i18n/callFlowLangs.js";
// The business's time zone, chosen at the start (4.3d.4): every account began on UTC, so its hours and times were off.
import TimeZonePicker from "../../common/TimeZonePicker";
import { browserZone } from "../../../utils/timeZones";

const BRAND = "#4F46E5";
const STEPS = [
  { label: "Company",     icon: BusinessOutlinedIcon },
  { label: "Departments", icon: ApartmentOutlinedIcon },
  { label: "Done",        icon: RocketLaunchOutlinedIcon },
];

const DEFAULT_DEPT = () => ({ name: "" });

export default function SetupWizard({ open, onComplete, onDismiss }) {
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation(["createplan", "business"]);
  // A language named in the screen's own language (CLDR), never the English name glued to a translated label.
  const languageName = (code) => {
    try { return new Intl.DisplayNames([i18n.language || "en"], { type: "language" }).of(code) || code; } catch { return code; }
  };
  const userId = useSelector((s) => s.auth?.user?.id);

  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // Step 1 state
  const [langs,   setLangs]   = useState(["en", "ar"]);
  const [locales, setLocales] = useState({});  // { en: "Company", ar: "الشركة", ... }
  const [businessType, setBusinessType] = useState("");
  const [zone, setZone] = useState(browserZone);

  // Step 2 state
  const [depts, setDepts] = useState([DEFAULT_DEPT()]);

  // Step 3 state
  const [savedProfile, setSavedProfile] = useState(false);
  const [savedDepts, setSavedDepts]     = useState(0);

  const toggleLang = (l) => {
    if (langs.includes(l)) {
      if (langs.length === 1) return;
      setLangs(langs.filter((x) => x !== l));
    } else {
      setLangs([...langs, l]);
    }
  };

  const addDept = () => setDepts((prev) => [...prev, DEFAULT_DEPT()]);
  const removeDept = (i) => setDepts((prev) => prev.filter((_, idx) => idx !== i));
  const updateDept = (i, value) =>
    setDepts((prev) => prev.map((d, idx) => (idx === i ? { ...d, name: value } : d)));

  const saveStep1 = useCallback(async () => {
    if (langs.some(l => !(locales[l] || "").trim())) {
      setError(t("wizNamesRequired"));
      return false;
    }
    if (!businessType) {
      setError(t("wizTypeRequired"));
      return false;
    }
    setError("");
    setSaving(true);
    try {
      const localesObj = {};
      for (const { code } of SUPPORTED_CALL_LANGS) {
        if (langs.includes(code)) {
          localesObj[code] = { name: (locales[code] || "").trim(), welcomeMessage: "", namePath: "", welcomeMessagePath: "" };
        }
      }
      const form = new FormData();
      form.append("payload", JSON.stringify({
        LocalesJson:         JSON.stringify(localesObj),
        Languages:           langs.join(","),
        DefaultLanguage:     langs.includes("en") ? "en" : langs[0],
        WorkingDays:         {},
        WorkingHours:        {},
        IsCustomWorkingDays: "false",
        Timezone:            zone || "UTC",
        audioFileNames:      [],
      }));
      await axiosInstance.post("/Profile", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      dispatch(getProfile());
      setSavedProfile(true);
    } catch (e) {
      setError(e.response?.data?.message || t("wizFailedSaveCompany"));
      setSaving(false);
      return false;
    }
    // The type, once the profile it is kept in exists. Its homes follow at once in the menu.
    try {
      await saveBusiness(userId, { type: businessType });
      return true;
    } catch {
      setError(t("wizFailedSaveType"));
      return false;
    } finally {
      setSaving(false);
    }
  }, [locales, langs, dispatch, businessType, userId, t, zone]);

  const saveStep2 = useCallback(async () => {
    const valid = depts.filter((d) => d.name.trim());
    setSaving(true);
    setError("");
    try {
      if (valid.length > 0) {
        const defLang = langs.includes("en") ? "en" : "ar";
        const payload = valid.map((d) => ({
          SectionName:         d.name.trim(),
          Path:                "",
          Route:               "",
          RouteType:           "SIP",
          Language:            defLang,
          WorkingDaysAndTimes: {},
          audioFileNames:      [],
        }));
        const form = new FormData();
        form.append("payload", JSON.stringify(payload));
        await axiosInstance.post("/CallFlow", form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        dispatch(getCallFlow());
        setSavedDepts(valid.length);
      }
      return true;
    } catch (e) {
      setError(e.response?.data?.message || t("wizFailedSaveDepts"));
      return false;
    } finally {
      setSaving(false);
    }
  }, [depts, langs, dispatch]);

  const handleNext = async () => {
    if (step === 0) {
      const ok = await saveStep1();
      if (ok) setStep(1);
    } else if (step === 1) {
      const ok = await saveStep2();
      if (ok) setStep(2);
    } else {
      onComplete("callflow");
    }
  };

  const handleDismiss = () => {
    localStorage.setItem("wizardSeen", "1");
    onDismiss();
  };

  return (
    <Dialog
      open={open}
      maxWidth="xs"
      fullWidth
      disableEscapeKeyDown={saving}
      PaperProps={{ sx: {
        borderRadius: "20px",
        background: "#fff",
        boxShadow: "0 24px 80px rgba(0,0,0,0.22)",
        overflow: "hidden",
      }}}
    >
      {/* ── Header strip ──────────────────────────────────────────── */}
      <Box sx={{
        background: `linear-gradient(135deg, ${BRAND} 0%, #7C3AED 100%)`,
        px: 3, pt: 3, pb: 2.5,
        position: "relative",
      }}>
        <IconButton
          size="small"
          onClick={handleDismiss}
          disabled={saving}
          sx={{ position: "absolute", top: 10, right: 10, color: "rgba(255,255,255,0.7)",
                "&:hover": { color: "#fff", background: "rgba(255,255,255,0.12)" } }}
        >
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
        <Typography sx={{ color: "#fff", fontWeight: 800, fontSize: "1.15rem", mb: 0.25 }}>
          {t("welcomeTitle")}
        </Typography>
        <Typography sx={{ color: "rgba(255,255,255,0.75)", fontSize: "0.8rem" }}>
          {step === 0 && t("step1Of3")}
          {step === 1 && t("step2Of3")}
          {step === 2 && t("allDone")}
        </Typography>

        {/* Step indicator dots */}
        <Box sx={{ display: "flex", gap: 0.75, mt: 2 }}>
          {STEPS.map((s, i) => (
            <Box key={s.label} sx={{
              height: 4,
              borderRadius: 2,
              flex: i <= step ? 2 : 1,
              background: i <= step ? "#fff" : "rgba(255,255,255,0.3)",
              transition: "flex 0.3s, background 0.3s",
            }} />
          ))}
        </Box>
      </Box>

      <DialogContent sx={{ px: 3, py: 2.5 }}>
        {/* ── Step 1: Company ─────────────────────────────────── */}
        {step === 0 && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Typography sx={{ fontSize: "0.78rem", color: "text.secondary", mb: 0.5 }}>
              {t("namesIvrHint")}
            </Typography>
            {SUPPORTED_CALL_LANGS.filter(({ code }) => langs.includes(code)).map(({ code, nativeName, dir }, i) => (
              <TextField
                key={code}
                label={t("companyNameIn", { language: languageName(code) })}
                value={locales[code] || ""}
                onChange={(e) => setLocales((prev) => ({ ...prev, [code]: e.target.value }))}
                size="small"
                fullWidth
                autoFocus={i === 0}
                placeholder={nativeName}
                inputProps={{ maxLength: 80, dir }}
              />
            ))}
            <Box>
              <Typography sx={{ fontSize: "0.72rem", fontWeight: 600, color: "text.secondary",
                                textTransform: "uppercase", letterSpacing: ".06em", mb: 0.75 }}>
                {t("ivrLanguageHeader")}
              </Typography>
              <Box sx={{ display: "flex", gap: 1 }}>
                {SUPPORTED_CALL_LANGS.map(({ code: key, nativeName: label }) => (
                  <Chip
                    key={key}
                    label={label}
                    size="small"
                    clickable
                    onClick={() => toggleLang(key)}
                    sx={{
                      fontWeight: langs.includes(key) ? 700 : 500,
                      background: langs.includes(key) ? alpha(BRAND, 0.1) : "transparent",
                      color: langs.includes(key) ? BRAND : "text.secondary",
                      border: `1.5px solid ${langs.includes(key) ? BRAND : "rgba(0,0,0,0.15)"}`,
                    }}
                  />
                ))}
              </Box>
            </Box>
            {/* THE BUSINESS'S TIME ZONE — where its hours run and its times are shown; from the browser, to confirm. */}
            <Box>
              <Typography sx={{ fontSize: "var(--t-overline)", fontWeight: 600, color: "var(--ink-500)",
                                textTransform: "uppercase", letterSpacing: ".06em", mb: "var(--sp-1)" }}>
                {t("wizTimeZone")}
              </Typography>
              <TimeZonePicker id="wizard-timezone" value={zone} onChange={setZone} />
              <Typography sx={{ fontSize: "var(--t-caption)", color: "var(--ink-500)", mt: "var(--sp-1)" }}>
                {t("wizTimeZoneHelp")}
              </Typography>
            </Box>
            {/* WHAT KIND OF BUSINESS — it decides which screens it starts with; changeable later in settings. */}
            <Box>
              <Typography sx={{ fontSize: "0.72rem", fontWeight: 600, color: "text.secondary",
                                textTransform: "uppercase", letterSpacing: ".06em", mb: 0.25 }}>
                {t("wizBusinessType")}
              </Typography>
              <Typography sx={{ fontSize: "0.72rem", color: "text.disabled", mb: 0.75 }}>
                {t("wizBusinessTypeHelp")}
              </Typography>
              <Box role="group" aria-label={t("wizBusinessType")} sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {BUSINESS_TYPES.map((type) => (
                  <Chip
                    key={type}
                    label={t(`business:type_${type}`)}
                    size="small"
                    clickable
                    aria-pressed={businessType === type}
                    onClick={() => { setBusinessType(type); setError(""); }}
                    sx={{
                      fontWeight: businessType === type ? 700 : 500,
                      background: businessType === type ? alpha(BRAND, 0.1) : "transparent",
                      color: businessType === type ? BRAND : "text.secondary",
                      border: `1.5px solid ${businessType === type ? BRAND : "rgba(0,0,0,0.15)"}`,
                    }}
                  />
                ))}
              </Box>
            </Box>
          </Box>
        )}

        {/* ── Step 2: Departments ──────────────────────────── */}
        {step === 1 && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <Typography sx={{ fontSize: "0.78rem", color: "text.secondary", mb: 0.5 }}>
              {t("addDeptsHint")}
            </Typography>
            {depts.map((d, i) => (
              <Box key={i} sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                <TextField
                  value={d.name}
                  onChange={(e) => updateDept(i, e.target.value)}
                  size="small"
                  fullWidth
                  placeholder={i === 0 ? t("deptPlaceholder0") : i === 1 ? t("deptPlaceholder1") : t("deptPlaceholderN")}
                  autoFocus={i === depts.length - 1 && i > 0}
                  inputProps={{ maxLength: 60 }}
                />
                {depts.length > 1 && (
                  <IconButton size="small" onClick={() => removeDept(i)}
                    sx={{ color: "text.disabled", "&:hover": { color: "#EF4444" } }}>
                    <CloseIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                )}
              </Box>
            ))}
            {depts.length < 8 && (
              <Button
                size="small"
                startIcon={<AddOutlinedIcon />}
                onClick={addDept}
                sx={{ alignSelf: "flex-start", textTransform: "none", color: BRAND,
                      fontWeight: 600, fontSize: "0.78rem" }}
              >
                {t("addDeptBtn")}
              </Button>
            )}
            <Typography sx={{ fontSize: "0.72rem", color: "text.disabled", mt: 0.5 }}>
              {t("skipDeptsHint")}
            </Typography>
          </Box>
        )}

        {/* ── Step 3: Done ─────────────────────────────────── */}
        {step === 2 && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, py: 1 }}>
            {[
              savedProfile && t("companyProfileSaved"),
              savedDepts > 0 && t("deptsCreated", { count: savedDepts }),
            ].filter(Boolean).map((msg) => (
              <Box key={msg} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CheckCircleIcon sx={{ fontSize: 18, color: "#22C55E" }} />
                <Typography sx={{ fontSize: "0.875rem", color: "text.primary" }}>{msg}</Typography>
              </Box>
            ))}
            <Typography sx={{ fontSize: "0.82rem", color: "text.secondary", mt: 1 }}>
              {t("goToCallFlowPre")} <strong>Call Flow</strong> {t("goToCallFlowPost")}
            </Typography>
          </Box>
        )}

        {error && (
          <Typography sx={{ fontSize: "0.78rem", color: "#EF4444", mt: 1.5 }}>{error}</Typography>
        )}
      </DialogContent>

      {/* ── Footer actions ──────────────────────────────────────── */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center",
                 px: 3, pb: 2.5, pt: 0 }}>
        {step < 2 ? (
          <Button
            size="small"
            onClick={handleDismiss}
            disabled={saving}
            sx={{ textTransform: "none", color: "text.disabled", fontSize: "0.78rem" }}
          >
            {t("setupLaterBtn")}
          </Button>
        ) : (
          <Box />
        )}
        <Button
          variant="contained"
          size="small"
          onClick={handleNext}
          disabled={saving}
          sx={{
            textTransform: "none", fontWeight: 700, borderRadius: "10px",
            background: BRAND, "&:hover": { background: "#4338CA" },
            minWidth: 100, px: 2.5, py: 0.875,
          }}
        >
          {saving
            ? <CircularProgress size={14} sx={{ color: "#fff" }} />
            : step === 1 && depts.every((d) => !d.name.trim())
              ? t("skipBtn")
              : step === 2
                ? t("openCallFlowBtn")
                : t("nextBtn")}
        </Button>
      </Box>
    </Dialog>
  );
}
